import { useEffect, useRef } from 'react'
import { useInterviewStore } from '@/stores/useInterviewStore'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { ThinkingIndicator } from './ThinkingIndicator'
import { ThinkingBlock } from './ThinkingBlock'
import ReactMarkdown from 'react-markdown'
import { streamBrainstorm, updateBuildSession } from '@/lib/api'

export function BrainstormChat() {
  const {
    sessionId,
    brainstormTurns,
    brainstormContent,
    brainstormStreamingContent,
    brainstormStreamingThinking,
    brainstormIsThinking,
    workflowStatus,
    buildMode,
    foolscapContent,
    foolscapStreamingContent,
    foolscapStreamingThinking,
    foolscapIsThinking,
    addBrainstormUserTurn,
    setWorkflowStatus,
    setError,
    setBuildMode,
    // Brainstorm streaming actions
    startBrainstormStreaming,
    updateBrainstormStreamingContent,
    setBrainstormCoachingMessage,
    startBrainstormThinking,
    updateBrainstormThinking,
    finishBrainstormThinking,
    commitBrainstormStreamingMessage,
    finishBrainstormStreaming,
    // Document streaming actions (line-by-line with frontend diff computation)
    startBrainstormDocumentStreaming,
    appendBrainstormDocumentLine,
    finishBrainstormDocumentStreaming,
    // Legacy: Instant document update - receives complete annotated document from backend
    updateBrainstormDocumentInstant,
    // Foolscap streaming actions
    startFoolscapStreaming,
    updateFoolscapStreamingContent,
    setFoolscapCoachingMessage,
    startFoolscapThinking,
    updateFoolscapThinking,
    finishFoolscapThinking,
    commitFoolscapStreamingMessage,
    finishFoolscapStreaming,
    // Foolscap document streaming actions (line-by-line with frontend diff computation)
    startFoolscapDocumentStreaming,
    appendFoolscapDocumentLine,
    finishFoolscapDocumentStreaming,
    // Legacy: Instant document update
    updateFoolscapDocumentInstant,
    // Scratchpad streaming actions (line-by-line with frontend diff computation)
    startScratchpadDocumentStreaming,
    appendScratchpadDocumentLine,
    finishScratchpadDocumentStreaming,
  } = useInterviewStore()

  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages arrive or content is streaming
  // Use instant scroll during streaming for smoother following, smooth scroll otherwise
  useEffect(() => {
    const isStreaming = workflowStatus === 'streaming'
    messagesEndRef.current?.scrollIntoView({
      behavior: isStreaming ? 'instant' : 'smooth',
      block: 'end'
    })
  }, [
    brainstormTurns,
    brainstormStreamingContent,
    brainstormStreamingThinking,
    foolscapStreamingContent,
    foolscapStreamingThinking,
    workflowStatus
  ])

  // NOTE: Auto-trigger removed - user starts the conversation themselves

  const handleSubmit = async (content: string) => {
    if (!content.trim() || workflowStatus === 'submitting' || workflowStatus === 'processing' || workflowStatus === 'streaming') {
      return
    }

    if (!sessionId) {
      setError('No session ID available')
      return
    }

    // Detect /foolscap command
    const isFoolscapCommand = content.trim().toLowerCase().startsWith('/foolscap')
    const effectiveMode = isFoolscapCommand ? 'foolscap' : buildMode

    // Switch mode if /foolscap command detected
    if (isFoolscapCommand && buildMode !== 'foolscap') {
      setBuildMode('foolscap')
    }

    // Add user message immediately
    addBrainstormUserTurn(content)
    setWorkflowStatus('submitting')

    try {
      // Start streaming based on effective mode
      if (effectiveMode === 'foolscap') {
        startFoolscapStreaming()
      } else {
        startBrainstormStreaming()
      }

      await streamBrainstorm(
        {
          content,
          session_id: sessionId,
          brainstorm_mode: effectiveMode === 'brainstorm',
          brainstorm_content: brainstormContent || undefined,
          foolscap_content: foolscapContent || undefined,
          mode: effectiveMode,
        },
        {
          onChunk: (chunk) => {
            if (effectiveMode === 'foolscap') {
              updateFoolscapStreamingContent(chunk)
            } else {
              updateBrainstormStreamingContent(chunk)
            }
          },
          onThinkingStart: () => {
            if (effectiveMode === 'foolscap') {
              startFoolscapThinking()
            } else {
              startBrainstormThinking()
            }
          },
          onThinkingDelta: (delta) => {
            if (effectiveMode === 'foolscap') {
              updateFoolscapThinking(delta)
            } else {
              updateBrainstormThinking(delta)
            }
          },
          onThinkingComplete: () => {
            if (effectiveMode === 'foolscap') {
              finishFoolscapThinking()
            } else {
              finishBrainstormThinking()
            }
          },
          // Coaching message arrives complete - frontend animates typing effect
          onCoachingMessage: (content) => {
            if (effectiveMode === 'foolscap') {
              // SET (not append) - message arrives complete
              setFoolscapCoachingMessage(content)
            } else {
              // SET (not append) - message arrives complete, TypewriterText animates
              setBrainstormCoachingMessage(content)
            }
          },
          onTextComplete: () => {
            // Text streaming done - commit message and unlock input
            if (effectiveMode === 'foolscap') {
              commitFoolscapStreamingMessage()
            } else {
              commitBrainstormStreamingMessage()
            }
          },
          // Document streaming (line-by-line for frontend diff computation)
          onDocumentStart: () => {
            if (effectiveMode === 'foolscap') {
              startFoolscapDocumentStreaming()
            } else {
              startBrainstormDocumentStreaming()
            }
          },
          onDocumentLine: (line) => {
            if (effectiveMode === 'foolscap') {
              appendFoolscapDocumentLine(line)
            } else {
              appendBrainstormDocumentLine(line)
            }
          },
          onDocumentComplete: (fullDocument) => {
            if (effectiveMode === 'foolscap') {
              finishFoolscapDocumentStreaming(fullDocument)
            } else {
              finishBrainstormDocumentStreaming(fullDocument)
            }
          },
          // Legacy: Instant document update - receives complete annotated document from backend
          onDocumentUpdate: (content) => {
            if (effectiveMode === 'foolscap') {
              updateFoolscapDocumentInstant(content)
            } else {
              updateBrainstormDocumentInstant(content)
            }
          },
          // Scratchpad streaming (line-by-line for frontend diff computation)
          onScratchpadStart: () => {
            startScratchpadDocumentStreaming()
          },
          onScratchpadLine: (line) => {
            appendScratchpadDocumentLine(line)
          },
          onScratchpadComplete: (fullScratchpad) => {
            finishScratchpadDocumentStreaming(fullScratchpad)
          },
          onDone: async (output) => {
            if (effectiveMode === 'foolscap') {
              finishFoolscapStreaming(output)
            } else {
              finishBrainstormStreaming(output)
            }

            // Persist frontend-computed annotated content to DB
            // The store has the annotated content after finish*Streaming runs
            const state = useInterviewStore.getState()
            const annotatedContent = effectiveMode === 'foolscap'
              ? state.foolscapContent
              : state.brainstormContent

            // Build updates object with document content and scratchpad
            const updates: Record<string, string> = {}

            if (annotatedContent) {
              if (effectiveMode === 'foolscap') {
                updates.foolscap_content = annotatedContent
              } else {
                updates.brainstorm_content = annotatedContent
              }
            }

            // Persist scratchpad content (used in both modes)
            const scratchpadContent = state.scratchpadDisplayContent || state.scratchpadContent
            if (scratchpadContent) {
              updates.scratchpad_content = scratchpadContent
            }

            if (sessionId && Object.keys(updates).length > 0) {
              try {
                await updateBuildSession(sessionId, updates)
              } catch (err) {
                console.error('Failed to persist content to DB:', err)
              }
            }
          },
          onError: (error) => {
            setError(error)
          },
        }
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Messages area */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-6">
        <div className="max-w-2xl py-6">
          {brainstormTurns.length === 0 && workflowStatus !== 'submitting' && workflowStatus !== 'streaming' ? (
            <div className="text-center py-12">
              <h2 className="text-lg font-light text-foreground mb-2">
                What do you want to explore?
              </h2>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Tell me what you're thinking about and I'll surface relevant cards from your library.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {brainstormTurns.map((turn, index) => (
                <ChatMessage key={index} turn={turn} />
              ))}
              {workflowStatus === 'submitting' && (
                <ThinkingIndicator />
              )}
              {workflowStatus === 'streaming' && (
                <div className="flex gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Show thinking block when actively thinking OR when we have thinking content to display */}
                    {((buildMode === 'brainstorm' && brainstormStreamingThinking) ||
                      (buildMode === 'foolscap' && foolscapStreamingThinking)) && (
                      <ThinkingBlock
                        thinking={(buildMode === 'foolscap' ? foolscapStreamingThinking : brainstormStreamingThinking) || ''}
                        isStreaming={buildMode === 'foolscap' ? foolscapIsThinking : brainstormIsThinking}
                      />
                    )}
                    {/* Show waiting indicator after thinking completes but before text arrives */}
                    {((buildMode === 'brainstorm' && !brainstormIsThinking && brainstormStreamingThinking && !brainstormStreamingContent?.coaching_message) ||
                      (buildMode === 'foolscap' && !foolscapIsThinking && foolscapStreamingThinking && !foolscapStreamingContent?.coaching_message)) && (
                      <div className="text-sm text-muted-foreground italic mt-3">
                        Preparing response...
                      </div>
                    )}
                    {/* Show text content as it streams in */}
                    {((buildMode === 'brainstorm' && brainstormStreamingContent?.coaching_message) ||
                      (buildMode === 'foolscap' && foolscapStreamingContent?.coaching_message)) && (
                      <div className="prose-zinsser text-sm">
                        <ReactMarkdown>
                          {buildMode === 'foolscap'
                            ? foolscapStreamingContent?.coaching_message || ''
                            : brainstormStreamingContent?.coaching_message || ''}
                        </ReactMarkdown>
                        <span className="inline-block w-1.5 h-4 bg-muted-foreground/50 animate-pulse ml-0.5" />
                      </div>
                    )}
                    {/* Show initial thinking indicator before any thinking content arrives */}
                    {((buildMode === 'brainstorm' && brainstormIsThinking && !brainstormStreamingThinking) ||
                      (buildMode === 'foolscap' && foolscapIsThinking && !foolscapStreamingThinking)) && (
                      <div className="text-sm text-muted-foreground italic flex items-center gap-2">
                        <span className="inline-block w-1.5 h-3 bg-muted-foreground/50 animate-pulse" />
                        Thinking...
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 py-4 pr-6">
        <div className="max-w-2xl">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <ChatInput
                onSubmit={handleSubmit}
                disabled={workflowStatus === 'submitting' || workflowStatus === 'processing' || workflowStatus === 'streaming'}
                placeholder={
                  buildMode === 'foolscap'
                    ? "Refine the Foolscap structure..."
                    : brainstormTurns.length === 0
                      ? "What direction interests you?"
                      : "Continue the conversation..."
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
